import { useState, useEffect } from 'react';
import { X, Check, Hash, ArrowRight, Inbox, Circle as CircleIcon, Square, Triangle, Diamond } from 'lucide-react';
import { getAnswerConfig } from '../../utils/answerConfig';

const LUCIDE_BY_NAME = {
  circle: CircleIcon,
  square: Square,
  triangle: Triangle,
  diamond: Diamond,
  check: Check,
  x: X,
};

export default function QuestionResults({
  question,
  questionNumber,
  totalQuestions,
  answerCounts,
  totalAnswers,
  correctAnswerIndex,
  questionStartTime,
  questionDuration = 30,
  sessionCode,
  onNext,
  onEndGame,
  onBack: _onBack,
  onExit,
  autoAdvanceCountdown,
  allAnswered = false,
}) {
  const [timeProgress, setTimeProgress] = useState(100);

  useEffect(() => {
    if (!questionStartTime) return;
    const interval = setInterval(() => {
      const now = new Date();
      const start = new Date(questionStartTime);
      const elapsed = (now - start) / 1000;
      const remaining = Math.max(0, questionDuration - elapsed);
      const progress = (remaining / questionDuration) * 100;
      setTimeProgress(progress);
    }, 100);
    return () => clearInterval(interval);
  }, [questionStartTime, questionDuration]);

  if (!question) return null;

  const timeExpired = timeProgress === 0;
  const revealed = timeExpired || allAnswered;
  const answerConfig = getAnswerConfig(question.type);
  const isTwoAnswers = question.answers?.length <= 2;

  return (
    <div className="bg-mp-bg-lightgreen font-body text-mp-darkgreen h-dvh w-dvw overflow-hidden flex flex-col">
      {/* Progress Bar */}
      <div className="h-2 w-full bg-mp-darkgreen/10 shrink-0 relative">
        <div
          className="h-full bg-mp-lime rounded-r-full transition-all duration-100"
          style={{ width: `${timeProgress}%` }}
        />
        {onExit && (
          <button
            onClick={onExit}
            className="absolute top-1 left-2 z-30 flex items-center justify-center w-8 h-8 rounded-full bg-mp-darkgreen/85 backdrop-blur-sm text-white hover:bg-mp-darkgreen transition-colors"
            title="Avslutt økt"
            aria-label="Avslutt økt"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="flex-1 flex flex-col w-full overflow-hidden">
        {/* Question Card */}
        <div className="shrink-0 w-full px-4 pt-3 pb-2">
          <div className="w-full bg-white rounded-2xl shadow-mp-2 px-5 py-3 mx-auto max-w-4xl flex items-center gap-3 justify-center flex-wrap border-l-[6px] border-mp-darkgreen">
            <span className="shrink-0 font-display text-[11px] text-mp-teal uppercase tracking-[0.18em] tabular-nums">
              {questionNumber} / {totalQuestions}
            </span>
            <span aria-hidden className="text-mp-darkgreen/20">·</span>
            <h1 className="font-display text-xl md:text-3xl text-mp-darkgreen text-center leading-tight">
              {question.text}
            </h1>
          </div>
        </div>

        {/* Image Area */}
        <div className="flex-1 min-h-0 w-full bg-mp-darkgreen p-2 relative">
          {question.imageUrl && (
            <div
              className="w-full h-full bg-contain bg-center bg-no-repeat rounded-xl"
              style={{ backgroundImage: `url("${question.imageUrl}")` }}
              role="img"
              aria-label=""
            />
          )}
          <div className="absolute top-4 right-4 inline-flex items-center gap-2.5 bg-mp-darkgreen text-white px-4 py-2 rounded-full shadow-mp-2">
            <Inbox size={16} className="text-mp-lime" />
            <span className="font-display text-xl text-mp-lime tabular-nums leading-none">{totalAnswers}</span>
            <span className="font-display text-[10px] uppercase tracking-[0.18em] text-white/70">Svar</span>
          </div>
        </div>

        {/* Answer Grid */}
        <div className="shrink-0 bg-white p-2 pb-safe flex flex-col gap-2 z-30">
          <div className={`grid grid-cols-2 gap-2 ${isTwoAnswers ? 'h-[18dvh] min-h-[140px]' : 'grid-rows-2 h-[24dvh] min-h-[180px]'}`}>
            {question.answers?.map((answer, index) => {
              const config = answerConfig[index];
              if (!config) return null;
              const LucideIcon = LUCIDE_BY_NAME[config.lucideName] || CircleIcon;
              const count = answerCounts[index] || 0;
              const isCorrect = index === correctAnswerIndex;

              return (
                <div
                  key={answer.id}
                  className={`relative ${config.color} ${config.textColor} rounded-2xl flex flex-col items-center justify-center p-4 shadow-mp-2 group overflow-hidden ${
                    isCorrect && revealed ? 'ring-4 ring-mp-magenta ring-offset-2 ring-offset-white' : ''
                  }`}
                >
                  <div className="absolute top-3 left-3 opacity-60">
                    <LucideIcon size={24} strokeWidth={2.5} />
                  </div>

                  {/* Count badge after reveal */}
                  {revealed && count > 0 && !isCorrect && (
                    <div className="absolute top-3 right-3 bg-white/22 backdrop-blur-sm font-display text-sm px-2.5 py-1 rounded-full min-w-[28px] text-center">
                      {count}
                    </div>
                  )}

                  {/* Correct indicator */}
                  {isCorrect && revealed && (
                    <div className="absolute top-3 right-3 bg-mp-magenta text-white p-1.5 rounded-full flex items-center gap-1 shadow-mp-2">
                      <Check size={16} strokeWidth={3} />
                      {count > 0 && <span className="font-display text-sm pr-1">{count}</span>}
                    </div>
                  )}

                  <span className="font-display text-lg md:text-xl text-center leading-tight z-10">
                    {answer.text}
                  </span>

                  <LucideIcon
                    size={120}
                    strokeWidth={1.5}
                    className="absolute -bottom-6 -right-4 opacity-15 pointer-events-none"
                  />
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center px-2 py-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-mp-bg-lightgreen rounded-full">
              <Hash size={14} className="text-mp-teal" />
              <span className="font-display text-xs text-mp-darkgreen tracking-[0.16em]">PIN: {sessionCode}</span>
            </div>
            <div className="flex items-center gap-2">
              {revealed && autoAdvanceCountdown !== null && autoAdvanceCountdown > 0 && (
                <span className="font-display text-xs text-mp-stone-500 tabular-nums">
                  Går vidare om {autoAdvanceCountdown}s
                </span>
              )}
              <button
                onClick={onEndGame}
                className="px-4 py-2 font-display text-sm text-mp-darkgreen hover:text-mp-magenta transition-colors"
              >
                Avslutt
              </button>
              <button onClick={onNext} className="mp-cta !py-2 !px-4 !text-sm group">
                <span>Neste</span>
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
